import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, MessageCircle } from "lucide-react";
import NetworkCanvas from "./NetworkCanvas";
import { scrollTo } from "./Navbar";
import { WA_NUMBER } from "../data/site";

const Line = ({ children, delay, accent }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`block ${accent ? "text-smipl-orange" : ""}`}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = ({ openContact }) => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 180]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,#0a1e3a_0%,#001020_55%,#000a18_100%)]" />
        <NetworkCanvas />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,#001020_92%)]" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 w-full max-w-6xl mx-auto px-6 pt-32 pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="font-mono text-xs tracking-[0.35em] uppercase text-smipl-orange mb-8 flex items-center gap-3"
          data-testid="hero-tagline"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-pulse-ring absolute inline-flex h-full w-full rounded-full bg-smipl-orange" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-smipl-orange" />
          </span>
          Your Trusted Service Partner
        </motion.p>

        <h1 className="font-display font-extrabold tracking-tight leading-[0.98] text-5xl sm:text-7xl lg:text-[6.5rem]" data-testid="hero-headline">
          <Line delay={0.7}>Technology.</Line>
          <Line delay={0.85} accent>Integrated.</Line>
          <Line delay={1.0}>Delivered.</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.35 }}
          className="mt-8 max-w-xl text-base md:text-lg text-slate-400 leading-relaxed"
        >
          SMIPL brings networks, security, collaboration, compute and sourcing together —
          so your entire technology ecosystem works as one.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.55 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-btn"
            onClick={() => scrollTo("#ecosystem")}
            className="group flex items-center gap-2 rounded-full bg-smipl-orange text-white px-7 py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300"
          >
            Explore Solutions
            <ArrowDown size={15} className="group-hover:translate-y-0.5 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-talk-btn"
            onClick={openContact}
            className="group flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white hover:border-smipl-orange hover:text-smipl-orange transition-all duration-300"
          >
            Start a Conversation
            <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-whatsapp-btn"
            onClick={() => (WA_NUMBER ? window.open(`https://wa.me/${WA_NUMBER}`, "_blank") : openContact())}
            className="flex items-center gap-2 text-sm font-semibold text-wa hover:text-white transition-colors duration-300"
          >
            <MessageCircle size={16} /> WhatsApp us
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="text-white/30">
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
